import React, { useState, useEffect } from 'react';
import {
    Box,
    Typography,
    Paper,
    Button,
    Grid,
    TextField,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    IconButton,
    Card,
    CardContent,
    CardActions,
    Divider,
    List,
    ListItem,
    ListItemText,
    ListItemSecondaryAction,
    Chip
} from '@mui/material';
import {
    Add as AddIcon,
    Edit as EditIcon,
    Delete as DeleteIcon,
    Class as ClassIcon,
    School as SchoolIcon
} from '@mui/icons-material';
import { toast } from 'react-toastify';
import { classAPI } from '../services/api';

const Classes = () => {
    const [classes, setClasses] = useState([]);
    const [loading, setLoading] = useState(false);

    // Dialogs
    const [openClassDialog, setOpenClassDialog] = useState(false);
    const [openSectionDialog, setOpenSectionDialog] = useState(false);
    const [selectedClass, setSelectedClass] = useState(null);

    // Forms
    const [classForm, setClassForm] = useState({ name: '', numericValue: '' });
    const [sectionForm, setSectionForm] = useState({ name: '', capacity: 40 });

    const [isEditing, setIsEditing] = useState(false);
    const [editId, setEditId] = useState(null);

    useEffect(() => {
        fetchClasses();
    }, []);

    const fetchClasses = async () => {
        setLoading(true);
        try {
            const res = await classAPI.getAll();
            setClasses(res.data.data);
        } catch (error) {
            toast.error('Failed to fetch classes');
        } finally {
            setLoading(false);
        }
    };

    // Class Handlers
    const handleOpenClassDialog = (cls = null) => {
        if (cls) {
            setIsEditing(true);
            setEditId(cls.id);
            setClassForm({ name: cls.name, numericValue: cls.numericValue });
        } else {
            setIsEditing(false);
            setEditId(null);
            setClassForm({ name: '', numericValue: '' });
        }
        setOpenClassDialog(true);
    };

    const handleSubmitClass = async () => {
        try {
            if (isEditing) {
                await classAPI.update(editId, classForm);
                toast.success('Class updated');
            } else {
                await classAPI.create({ ...classForm, academicYearId: 1 }); // Default AY for now
                toast.success('Class created');
            }
            setOpenClassDialog(false);
            fetchClasses();
        } catch (error) {
            toast.error(error.response?.data?.message || 'Operation failed');
        }
    };

    const handleDeleteClass = async (id) => {
        if (window.confirm('Are you sure? This will deactivate the class.')) {
            try {
                await classAPI.delete(id);
                toast.success('Class deleted');
                fetchClasses();
            } catch (error) {
                toast.error('Failed to delete class');
            }
        }
    };

    // Section Handlers
    const handleOpenSectionDialog = (cls) => {
        setSelectedClass(cls);
        setSectionForm({ name: '', capacity: 40 });
        setOpenSectionDialog(true);
    };

    const handleSubmitSection = async () => {
        try {
            await classAPI.createSection(selectedClass.id, { ...sectionForm, academicYearId: 1 });
            toast.success('Section added');
            setOpenSectionDialog(false);
            fetchClasses();
        } catch (error) {
            toast.error('Failed to add section');
        }
    };

    const handleDeleteSection = async (classId, sectionId) => {
        if (window.confirm('Are you sure?')) {
            try {
                await classAPI.deleteSection(classId, sectionId);
                toast.success('Section deleted');
                fetchClasses();
            } catch (error) {
                toast.error('Failed to delete section');
            }
        }
    };

    return (
        <Box>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 3 }}>
                <Typography variant="h4" fontWeight="bold">Classes & Sections</Typography>
                <Button startIcon={<AddIcon />} variant="contained" onClick={() => handleOpenClassDialog()}>
                    Add Class
                </Button>
            </Box>

            <Grid container spacing={3}>
                {classes.map((cls) => (
                    <Grid item xs={12} md={6} lg={4} key={cls.id}>
                        <Card variant="outlined">
                            <CardContent>
                                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                                    <Box sx={{ display: 'flex', alignItems: 'center' }}>
                                        <ClassIcon color="primary" sx={{ mr: 1 }} />
                                        <Typography variant="h6">{cls.name}</Typography>
                                    </Box>
                                    <Box>
                                        <IconButton size="small" onClick={() => handleOpenClassDialog(cls)}><EditIcon /></IconButton>
                                        <IconButton size="small" color="error" onClick={() => handleDeleteClass(cls.id)}><DeleteIcon /></IconButton>
                                    </Box>
                                </Box>

                                <Divider sx={{ my: 1 }} />

                                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mt: 2, mb: 1 }}>
                                    <Typography variant="subtitle2" color="textSecondary">Sections ({cls.sections?.length || 0})</Typography>
                                    <Button startIcon={<AddIcon />} size="small" onClick={() => handleOpenSectionDialog(cls)}>
                                        Add Section
                                    </Button>
                                </Box>

                                <List dense>
                                    {cls.sections?.map((sec) => (
                                        <ListItem key={sec.id} sx={{ bgcolor: 'background.default', borderRadius: 1, mb: 1 }}>
                                            <ListItemText
                                                primary={`Section ${sec.name}`}
                                                secondary={`Capacity: ${sec.capacity}`}
                                            />
                                            <ListItemSecondaryAction>
                                                <IconButton edge="end" size="small" onClick={() => handleDeleteSection(cls.id, sec.id)}>
                                                    <DeleteIcon fontSize="small" />
                                                </IconButton>
                                            </ListItemSecondaryAction>
                                        </ListItem>
                                    ))}
                                    {(!cls.sections || cls.sections.length === 0) && (
                                        <Typography variant="body2" color="textSecondary" sx={{ fontStyle: 'italic', textAlign: 'center', py: 2 }}>
                                            No sections added
                                        </Typography>
                                    )}
                                </List>
                            </CardContent>
                        </Card>
                    </Grid>
                ))}
            </Grid>

            {/* Class Dialog */}
            <Dialog open={openClassDialog} onClose={() => setOpenClassDialog(false)}>
                <DialogTitle>{isEditing ? 'Edit Class' : 'Add New Class'}</DialogTitle>
                <DialogContent>
                    <TextField
                        autoFocus
                        margin="normal"
                        label="Class Name"
                        fullWidth
                        placeholder="e.g. Class 10"
                        value={classForm.name}
                        onChange={(e) => setClassForm({ ...classForm, name: e.target.value })}
                    />
                    <TextField
                        margin="normal"
                        label="Numeric Value"
                        type="number"
                        fullWidth
                        placeholder="e.g. 10"
                        helperText="Used for sorting"
                        value={classForm.numericValue}
                        onChange={(e) => setClassForm({ ...classForm, numericValue: e.target.value })}
                    />
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setOpenClassDialog(false)}>Cancel</Button>
                    <Button variant="contained" onClick={handleSubmitClass}>Save</Button>
                </DialogActions>
            </Dialog>

            {/* Section Dialog */}
            <Dialog open={openSectionDialog} onClose={() => setOpenSectionDialog(false)}>
                <DialogTitle>Add Section to {selectedClass?.name}</DialogTitle>
                <DialogContent>
                    <TextField
                        autoFocus
                        margin="normal"
                        label="Section Name"
                        fullWidth
                        placeholder="e.g. A"
                        value={sectionForm.name}
                        onChange={(e) => setSectionForm({ ...sectionForm, name: e.target.value })}
                    />
                    <TextField
                        margin="normal"
                        label="Capacity"
                        type="number"
                        fullWidth
                        value={sectionForm.capacity}
                        onChange={(e) => setSectionForm({ ...sectionForm, capacity: e.target.value })}
                    />
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setOpenSectionDialog(false)}>Cancel</Button>
                    <Button variant="contained" onClick={handleSubmitSection}>Save</Button>
                </DialogActions>
            </Dialog>
        </Box>
    );
};

export default Classes;
